import { useCallback, useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { CircularProgress } from '@mui/material';
import useFeatureFlags from 'api/useFeatureFlags';
import useIntegration from 'api/useIntegration';
import usePermissions from 'api/usePermissions';
import usePublisher from 'api/usePublisher';
import { useUserDetails } from 'api/useUserDetails';
import {
  EPublisherSolutionModel,
  EPublisherSolutionModelSubType,
  Portal,
  PublisherInfo
} from 'common/contracts';
import { EPermissionGroup } from 'common/permissions.types';
import { EFeatureFlag, ELocalStorageKeys } from 'constants/enums';
import { usePortalStoreInfo } from 'hooks/usePortalStoreInfo';
import { useProfilesManager } from 'hooks/useProfilesManager';
import { ERouteContext } from 'routes/types';
import { useChecklistState } from 'state/hooks/setup-checklist.hook';
import { localStorageUtil } from 'utils/localStorageUtil';
import { permissionsUtil } from 'utils/permissionsUtil';
import { EToasterStatus, showToast } from 'utils/showToast';

import { cn } from 'components/lib/utils';

import PageInactiveBanner from '../IntegrationSettings/PageInactiveBanner';

import { AppsFlyerSection } from './components/AppsFlyerSection';
import { AuthPlayerSection } from './components/AuthPlayerSection';
import { CredentialsSection } from './components/CredentialsSection';
import { EventsWebhookSection } from './components/EventsWebhookSection';
import { GrantAwardSection } from './components/GrantAwardSection';
import { InitiateGameAuthSection } from './components/InitiateGameAuthSection';
import { IntegrationEnvironmentsSection } from './components/IntegrationEnvironmentsSection';
import { PersonalizeSection } from './components/PersonalizeSection';
import { PortalInfoSection } from './components/PortalInfoSection';
import { ProjectInfoSection } from './components/ProjectInfoSection';
import { StoreProjectInfoSection } from './components/StoreProjectInfoSection';
import { toastMessages } from './DeveloperSettings.constants';
import { ESavingForm, ExtendedIntegrationData } from './DeveloperSettings.types';
import { determineIsLoading } from './helpers/developerSettings.helpers';

interface IDeveloperSettingsProps {
  context: ERouteContext;
}

export const DeveloperSettings: React.FC<IDeveloperSettingsProps> = ({ context }) => {
  const { publisherId, portalId } = useParams<{ publisherId: string; portalId: string }>();
  const { isSuperAdmin, isAdmin, getPermissions } = usePermissions();
  const permissions = getPermissions();
  const { getPublisher, updatePublisher } = usePublisher(publisherId || undefined);
  const { getIntegration, updateIntegration } = useIntegration(publisherId as string);
  const {
    isProfilesEnabled,
    isToggleInProgress,
    toggleIntegrationProfiles,
    isProfilesFeatureFlagOn
  } = useProfilesManager();
  const { isPageInactive, portalDashboardIntegrationUrl } = usePortalStoreInfo();
  const { featureFlags } = useFeatureFlags({ publisherId });
  const { hasCategories, setTaskStatus, isWebStore, isWebCheckout } = useChecklistState();
  const { userDetails } = useUserDetails();

  const isCheckoutUser =
  localStorageUtil.get(ELocalStorageKeys.PUBLISHER_SOLUTION_MODEL) ===
  EPublisherSolutionModel.CHECKOUT;
  const isPortalContext = context === ERouteContext.PORTAL;

  const portalData = useMemo(() => {
    if (!isPortalContext || !userDetails?.portals) {
      return undefined;
    }
    return userDetails.portals.find((portal: Portal) => portal.portalId === portalId);
  }, [isPortalContext, userDetails?.portals, portalId]);

  const publisherData = getPublisher?.data as PublisherInfo | undefined;
  const integrationData = getIntegration?.data as ExtendedIntegrationData | undefined;
  const integrationRefetch = getIntegration?.refetch;

  const [savingForm, setSavingForm] = useState<ESavingForm>(ESavingForm.NONE);

  const isPaymentLinkProject = useMemo(
    () =>
      publisherData?.solutionModelSubtype ===
      EPublisherSolutionModelSubType.PAYMENT_LINKS,
    [publisherData?.solutionModelSubtype]
  );

  const isEventsV2FeatureFlagOn = featureFlags?.[EFeatureFlag.DASHBOARD_EVENTS_V2];
  const isAwardingV2FeatureFlagOn = featureFlags?.Dashboard_awarding_v2_rollout;
  const isAppsFlyerFeatureFlagOn = featureFlags?.dashboard_appsflyer;

  const isCompanyTabShownForPublisher = useMemo(
    () =>
      !permissionsUtil.shouldHideGroupInPermission(
        permissions,
        EPermissionGroup.COMPANY,
        publisherData?._id
      ),
    [permissions, publisherData?._id]
  );

  const canShowStoreCompanyTab = useCallback(
    (storeId: string) =>
      !permissionsUtil.shouldHideGroupInPermission(
        permissions,
        EPermissionGroup.COMPANY,
        storeId
      ),
    [permissions]
  );

  const isIntegrationTabShown = useMemo(
    () =>
      !permissionsUtil.shouldHideGroupInPermission(
        permissions,
        EPermissionGroup.INTEGRATION,
        publisherData?._id
      ),
    [permissions, publisherData?._id]
  );

  const handleToggleJwtSecret = useCallback(() => {
    const newValue = !integrationData?.deeplinkOn;
    updateIntegration.mutate(
      { deeplinkOn: newValue },
      {
        onSuccess: () => {
          showToast({
            message: newValue ? toastMessages.jwtEnabled : toastMessages.jwtDisabled,
            status: EToasterStatus.SUCCESS
          });
          getIntegration.refetch();
        },
        onError: () => {
          showToast({
            message: toastMessages.jwtError,
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  }, [integrationData?.deeplinkOn, updateIntegration, getIntegration]);

  useEffect(() => {
    return () => {
      integrationRefetch?.();
    };
  }, [integrationRefetch]);

  const handleCopy = useCallback((value: string, label: string): void => {
    navigator.clipboard.writeText(value);
    showToast({
      message: `${label} copied to clipboard`,
      status: EToasterStatus.SUCCESS
    });
  }, []);

  const filteredStores = useMemo(() => {
    if (!portalData?.stores) {
      return [];
    }
    return portalData.stores.filter((store) => canShowStoreCompanyTab(store.storeId));
  }, [portalData?.stores, canShowStoreCompanyTab]);

  const isLoading = determineIsLoading(isPortalContext, portalData, getPublisher);

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-340px)] w-full items-center justify-center">
        <CircularProgress title="Loading..." />
      </div>
    );
  }

  const contentClassName = cn(
    'w-full space-y-5 pb-5',
    isPageInactive && 'opacity-50 pointer-events-none'
  );

  return (
    <div className="w-full">
      {isPageInactive && (
        <div className="mb-4">
          <PageInactiveBanner
            message="Developer Settings are configured in the Portal Dashboard for stores connected to a portal."
            linkText="Portal Dashboard"
            linkUrl={portalDashboardIntegrationUrl}
          />
        </div>
      )}
      <div className={contentClassName}>
      {isPortalContext && (
        <>
          <PortalInfoSection
            portalData={portalData}
            portalId={portalId}
            savingForm={savingForm}
            setSavingForm={setSavingForm}
            isAdmin={isAdmin}
            onCopy={handleCopy}
          />
          {filteredStores.map((store) => (
            <StoreProjectInfoSection
              key={store.storeId}
              storeId={store.storeId}
              storeName={store.storeName}
              savingForm={savingForm}
              setSavingForm={setSavingForm}
              isSuperAdmin={isSuperAdmin}
              hasCategories={hasCategories}
              isWebStore={isWebStore}
              isWebCheckout={isWebCheckout}
              setTaskStatus={setTaskStatus}
              onCopy={handleCopy}
            />
          ))}
        </>
      )}

      {!isPortalContext && isCompanyTabShownForPublisher && (
        <ProjectInfoSection
          publisherData={publisherData}
          updatePublisher={updatePublisher}
          getPublisher={getPublisher}
          savingForm={savingForm}
          setSavingForm={setSavingForm}
          isSuperAdmin={isSuperAdmin}
          hasCategories={hasCategories}
          isWebStore={isWebStore}
          isWebCheckout={isWebCheckout}
          setTaskStatus={setTaskStatus}
          onCopy={handleCopy}
        />
      )}

      {isIntegrationTabShown && (
        <>
          <CredentialsSection
            integrationData={integrationData}
            isPaymentLinkProject={isPaymentLinkProject}
            isToggleInProgress={isToggleInProgress}
            onCopy={handleCopy}
            onToggleJwtSecret={handleToggleJwtSecret}
          />

          {isProfilesFeatureFlagOn && (
            <IntegrationEnvironmentsSection
              isProfilesEnabled={isProfilesEnabled}
              isToggleInProgress={isToggleInProgress}
              onToggle={toggleIntegrationProfiles}
            />
          )}

          {!isCheckoutUser && !isPaymentLinkProject && (<AuthPlayerSection
            publisherId={publisherId || ''}
            integrationData={integrationData}
            updateIntegration={updateIntegration}
            getIntegration={getIntegration}
            savingForm={savingForm}
            setSavingForm={setSavingForm}
            isProfilesEnabled={isProfilesEnabled}
            hasCategories={hasCategories}
            isWebStore={isWebStore}
            isWebCheckout={isWebCheckout}
            setTaskStatus={setTaskStatus}
          />)}

          {!isCheckoutUser && !isPaymentLinkProject && (<InitiateGameAuthSection
            publisherId={publisherId || ''}
            integrationData={integrationData}
            updateIntegration={updateIntegration}
            getIntegration={getIntegration}
            savingForm={savingForm}
            setSavingForm={setSavingForm}
            isProfilesEnabled={isProfilesEnabled}
          />)}

          <GrantAwardSection
            publisherId={publisherId || ''}
            integrationData={integrationData}
            updateIntegration={updateIntegration}
            getIntegration={getIntegration}
            savingForm={savingForm}
            setSavingForm={setSavingForm}
            isProfilesEnabled={isProfilesEnabled}
            hasCategories={hasCategories}
            isWebStore={isWebStore}
            isWebCheckout={isWebCheckout}
            setTaskStatus={setTaskStatus}
            isAwardingV2FeatureFlagOn={!!isAwardingV2FeatureFlagOn}
          />

          {!isCheckoutUser && !isPaymentLinkProject && (
            <PersonalizeSection
              publisherId={publisherId || ''}
              integrationData={integrationData}
              updateIntegration={updateIntegration}
              getIntegration={getIntegration}
              savingForm={savingForm}
              setSavingForm={setSavingForm}
              isProfilesEnabled={isProfilesEnabled}
              hasCategories={hasCategories}
              isWebStore={isWebStore}
              isWebCheckout={isWebCheckout}
              setTaskStatus={setTaskStatus}
            />
          )}

          {isAppsFlyerFeatureFlagOn && (
            <AppsFlyerSection
              integrationData={integrationData}
              updateIntegration={updateIntegration}
              getIntegration={getIntegration}
              savingForm={savingForm}
              setSavingForm={setSavingForm}
              isProfilesEnabled={isProfilesEnabled}
            />
          )}

          <EventsWebhookSection
            publisherId={publisherId || ''}
            integrationData={integrationData}
            updateIntegration={updateIntegration}
            getIntegration={getIntegration}
            savingForm={savingForm}
            setSavingForm={setSavingForm}
            isProfilesEnabled={isProfilesEnabled}
            isEventsV2FeatureFlagOn={!!isEventsV2FeatureFlagOn}
          />
        </>
      )}
      </div>
    </div>
  );
};
